import { Box, Button, Chip, List, MenuItem, Paper, Stack, Typography } from "@mui/material";
import { styled } from "@mui/system";
import { LoadingBox } from "@northpowerdev/np-react-utils";
import { formatISO } from "date-fns";
import { useEffect } from "react";
import { useContext } from "react";
import { useState } from "react";
import { asyncFetchMahiBff } from "../../../data/hikoFetch";
import { DebugBox } from "@northpowerdev/np-react-utils";
import { GrayTextField } from "../../../utils/GrayTextField";
import { plural, safeFormatDate } from "../../../utils/utilityFunctions";
import { MahiContext } from "../../state/MahiContext";

const NotesItemBox = styled(Box)(({theme}) => ({
    paddingTop: '0.5em',
    borderTop: '2px solid #fff',
    marginBottom: '1em',
}));

const NoteContentBox = styled(Box)(({theme}) => ({
    padding: '1em',
    background: '#f1f2f3',
    borderRadius: '1em',
}));

/**
 * 
 * @param {object} props E.g. {
 * notes: [ { "postedAt": "2022-10-18T11:08:57+13:00", "postedBy": { "id": "0057F0000036CRxQAM", "name": "IS Admin", "email": "ryan.wirtanen@northpower.com", "phone": "+64 21352479", "companyName": "Northpower Ltd", "userType": "Standard", "accountId": null, "contactId": null }, "atMentions": [], "content": "test post", "comments": [] }, ... ]
 * } 
 * @returns 
 */
export default function WorkOrderNotesPanel({workOrderId, icpNumber, isLoading, notes, noteAdded}) {

    const notesLength = notes?.length || 0
    const notesPlural = plural(notesLength)
    const isAre = notesPlural ? 'are' : 'is'

    return (
        <Paper sx={{p:2}}>
            <Typography variant="h5" color="secondary">Notes and Replies</Typography>
            <Typography variant="body1">There {isAre} {notesLength} note{notesPlural} for your work order.</Typography>

            <NotesList sx={{my:1}} notes={notes} />
            <AddNoteLink sx={{my:1}} workOrderId={workOrderId} noteSaved={(noteObject) => noteAdded(noteObject)} />
            <LoadingBox loading={isLoading} message="Loading notes for your work order">
                <DebugBox title="notes" stringify>{notes}</DebugBox>
            </LoadingBox>
        </Paper>


    )
}

function NotesList({notes, sx}) {
    if (!notes || !notes.length) {
        return null
    }
    return (
        <List sx={sx}>
            {
                notes.map(({postedAt, postedBy, atMentions, content, comments}) => {
                    return (
                        <NotesListItem sx={{mb:2}} key={postedAt} postedAt={postedAt} postedBy={postedBy} atMentions={atMentions} content={content} comments={comments} />
                    )
                })
            }
        </List>
    )
}

function NotesListItem({postedAt, postedBy, atMentions, content, comments, sx}) {
    const dateFormat = 'd MMMM yyyy HH:ii'

    const postedByName = postedBy?.name === 'Azure Integration' ? 'Inspector' : postedBy?.name
    const align = postedByName === 'Inspector' ? 'left' : 'right'
    return (
        <NotesItemBox sx={sx}>
            <NoteContentBox>
                <Typography variant="body2" align={align}><small>{safeFormatDate(postedAt, dateFormat)}</small></Typography>
                <Typography variant="body1" align={align} paragraph>{postedByName}</Typography>
                <Typography variant="body2" paragraph>{content}</Typography>
                {
                    atMentions?.length > 0
                    &&
                    <Typography variant="body2" sx={{fontSize: '0.8em'}}>
                        Mentions:&nbsp;
                        {
                            atMentions.map(({name}) => { // {id, name, email, phone, companyName, userType, accountId, contactId}
                                return name
                            }).join(', ')
                        }
                    </Typography>
                }
            </NoteContentBox>
            {
                comments?.length > 0
                &&
                <NotesList sx={{mt: 1, ml: 2, pl:2, borderLeft: '2px solid #ddd'}} notes={comments} />
            }
            <DebugBox title="atMentions" stringify>{atMentions}</DebugBox>
        </NotesItemBox>
    )
}


function AddNoteLink({workOrderId, noteSaved}) {
    const [isOpen, setIsOpen] = useState(false)

    const handleNoteSaved = (noteObject) => {
        setIsOpen(false)
        noteSaved(noteObject)
    }

    return (
        isOpen
        ?
        <AddNoteForm workOrderId={workOrderId} noteSaved={handleNoteSaved} />
        :
        <Button variant="text" onClick={() => setIsOpen(true)}>Add Note</Button>
    )
}

/**
 * 
 * @param {props} {
 *   workOrderId {string} e.g. 00017017,
 *   noteSaved {function} called with note text after note is saved.
 * } 
 * @returns 
 */
function AddNoteForm({workOrderId, noteSaved}) {
    
    const [noteText, setNoteText] = useState('')
    const [noteTextSaving, setNoteTextSaving] = useState(false)
    const [noteMentionsList, setNoteMentionList] = useState([])
    const [atMentionsList, setAtMentionsList] = useState([])
    const [atMentionsLoading, setAtMentionsLoading] = useState(false)
    const [selectedAtMention, setSelectedAtMention] = useState('')
    const mahiContext = useContext(MahiContext)

    const handleSaveNote = (event) => {
        event.preventDefault()
        console.log('handleSaveNote')
        const postBody = {
            mentionUserIds: noteMentionsList.map(m => m.id),
            content: noteText
        }
        asyncFetchMahiBff(mahiContext,
            `/bff/icp-livening-service/api/new-connections/work-order/${workOrderId}/add-post`,
            setNoteTextSaving,
            handleAddPostResponse,
            {},
            {
                method: 'POST',
                body: JSON.stringify(postBody)
            },
            'There was a problem saving your note.',
        )
    }

    const handleAddPostResponse = (response) => {
        console.log('handleAddPostResponse response', response)
        const fakePostObject = {
            postedAt: formatISO(new Date()),
            postedBy: {
                id: (new Date()).getTime(),
                name: "Inspector",
            },
            atMentions: noteMentionsList || [],
            content: noteText,
            comments: []
        }
        noteSaved(fakePostObject)
    }

    const handleNoteTextChange = (event) => {
        console.log('handleNoteTextChange')
        setNoteText(event.target.value)
    }

    const handleAtMentionChange = (event) => {
        console.log('handleAtMentionChange event', event.target.value)
        setSelectedAtMention(event.target.value) // E.g. Danielle Cave
        const selectedMentionObject = atMentionsList.find((m) => m.name === event.target.value)
        const alreadyMentioned = noteMentionsList.find((m) => m.name === event.target.value)
        if (selectedMentionObject && !alreadyMentioned) {
            const newNoteMentionsList = [...noteMentionsList]
            newNoteMentionsList.push(selectedMentionObject)
            console.log('handleAtMentionChange newNoteMentionsList', newNoteMentionsList)
            setNoteMentionList(newNoteMentionsList)
        }
    }

    const handleAtMentionDelete = (someMention) => { // E.g. Danielle Cave
        console.log('handleAtMentionDelete someMention', someMention)
        const newNoteMentionsList = noteMentionsList.filter((m) => m.name !== someMention)
        console.log('handleAtMentionDelete newNoteMentionsList', newNoteMentionsList)
        setNoteMentionList(newNoteMentionsList)
    }

    useEffect(() => {
        asyncFetchMahiBff(mahiContext, `/bff/icp-livening-service/api/new-connections/work-order/posts/get-users`, setAtMentionsLoading, setAtMentionsList)
    }, [mahiContext])

    return (
        <LoadingBox loading={noteTextSaving}>
            <form onSubmit={handleSaveNote}>
                <Box sx={{mb: 2}}>
                    <GrayTextField
                        label="Notes"
                        value={noteText}
                        fullWidth
                        multiline
                        minRows="2"
                        maxRows="6"
                        // helperText={icpNumberHelperText}
                        onChange={handleNoteTextChange}
                    />
                </Box>

                <LoadingBox loading={atMentionsLoading}>

                    <Stack direction="row" spacing={1} sx={{ my: 1, flexWrap: 'wrap', gap: 1 }}>
                    {
                        noteMentionsList.map((thisMention) => {
                            return (
                                <Chip key={thisMention.id} label={thisMention.name} variant="outlined" onDelete={() => handleAtMentionDelete(thisMention.name)} />
                            )
                        })
                    }
                    </Stack>

                    <GrayTextField
                        select
                        sx={{minWidth: '15em'}}
                        value={selectedAtMention}
                        label="Mention"
                        variant="outlined"
                        onChange={handleAtMentionChange}
                    >
                        {atMentionsList && atMentionsList.map((thisMention) => (
                            <MenuItem key={thisMention.id} value={thisMention.name}>
                                {thisMention.name}
                            </MenuItem>
                        ))}
                    </GrayTextField>

                </LoadingBox>

                <Button type="submit" variant="contained" onClick={handleSaveNote} sx={{mt: 2}}>Save Note</Button>
            </form>
        
            <DebugBox sx={{my: 2}} title="noteMentionsList" stringify>{noteMentionsList}</DebugBox>
            <DebugBox sx={{my: 2}} title="atMentionsList" stringify>{atMentionsList}</DebugBox>
        </LoadingBox>
    )
}